// Phone screen-time analytics: one snapshot per day (phoneUsage/{date}), a goal per metric, and a
// Whoop-style day score. Pure — the native side hands over raw foreground sessions, this turns them into
// the day record and everything the report draws from it.
//
// Day snapshot, v2 (from sessions; v1 = totals only, still read everywhere):
//   { date, v: 2, at, totalMs, opens, pickups, firstAt, lastAt, longestMs,
//     hours: [24 × ms], pickupHours: [24 × n],
//     apps: [{ pkg, label, ms, opens, longestMs, hours: [24 × ms] }]   (most used first, top 30)
//     sess: [[appIndex, startSec, durSec]] }                            (startSec from local midnight)
//
// Score: three metrics, each against its goal — screen time, time in distracting apps, pickups.
//   metricScore: 0 → 100, at goal → 80, 2 × goal → 0 (straight lines between). Weights 40 / 40 / 20.
//   A metric with no data (v1 days have no pickups) drops out and the others are re-weighted.
//   Zones like Whoop's recovery: green ≥ 67, yellow 34–66, red ≤ 33.

import { localDateStr } from './time.js';

const HOUR = 3600000;
const DAY_APPS = 30;
const MAX_SESS = 2000;

export const PHONE_GOAL_DEFAULTS = { screenMin: 180, distractMin: 60, pickups: 80 };
export const PHONE_METRICS = [
  { key: 'screen', label: 'Screen time', goalKey: 'screenMin', unit: 'ms', weight: 0.4 },
  { key: 'distract', label: 'Distracting apps', goalKey: 'distractMin', unit: 'ms', weight: 0.4 },
  { key: 'pickups', label: 'Pickups', goalKey: 'pickups', unit: 'n', weight: 0.2 },
];

// ── Dates ────────────────────────────────────────────────────────────────────────────────────────
export function phoneDayBounds(date) {
  const d = new Date(`${date}T00:00:00`);
  const start = d.getTime();
  d.setDate(d.getDate() + 1);
  return [start, d.getTime()];
}

export function addDaysStr(date, n) {
  const d = new Date(`${date}T12:00:00`);
  d.setDate(d.getDate() + n);
  return localDateStr(d);
}

/** Every date from `from` to `to` inclusive (YYYY-MM-DD). */
export function dateRange(from, to) {
  const out = [];
  for (let d = from; d <= to && out.length < 800; d = addDaysStr(d, 1)) out.push(d);
  return out;
}

// ── Building a day ───────────────────────────────────────────────────────────────────────────────
/** Same app again within `gapMs` (an in-app screen change, a notification pulled down) = one session. */
export function mergeSessions(raw = [], gapMs = 5000) {
  const list = (raw || []).filter((s) => s && s.pkg && s.end > s.start).map((s) => ({ pkg: s.pkg, start: s.start, end: s.end }))
    .sort((a, b) => a.start - b.start);
  const out = [];
  for (const s of list) {
    const last = out[out.length - 1];
    if (last && last.pkg === s.pkg && s.start - last.end <= gapMs) last.end = Math.max(last.end, s.end);
    else out.push(s);
  }
  return out;
}

/** Spread [start, end) over the day's 24 hour buckets (local hours). */
function addToHours(hours, start, end, dayStart) {
  let t = start;
  while (t < end) {
    const h = Math.floor((t - dayStart) / HOUR);
    if (h < 0 || h > 23) break;
    const edge = Math.min(end, dayStart + (h + 1) * HOUR);
    hours[h] += edge - t;
    t = edge;
  }
}

/**
 * Raw native data → the v2 day snapshot, or null when there's nothing.
 * native = { sessions: [{ pkg, start, end }], unlocks: [ms], wakes: [ms], labels: { pkg: label }, home, self }
 */
export function buildPhoneDay(date, native, at = Date.now()) {
  if (!native || !Array.isArray(native.sessions)) return null;
  const [dayStart, dayEnd] = phoneDayBounds(date);
  const skip = new Set([native.home, native.self].filter(Boolean));
  const labels = native.labels || {};
  const sessions = mergeSessions(native.sessions)
    .map((s) => ({ ...s, start: Math.max(s.start, dayStart), end: Math.min(s.end, dayEnd) }))
    .filter((s) => s.end > s.start && !skip.has(s.pkg));
  const taps = (Array.isArray(native.unlocks) && native.unlocks.length ? native.unlocks : native.wakes || [])
    .filter((t) => t >= dayStart && t < dayEnd);
  if (!sessions.length && !taps.length) return null;

  const byPkg = new Map();
  const hours = new Array(24).fill(0);
  for (const s of sessions) {
    let a = byPkg.get(s.pkg);
    if (!a) { a = { pkg: s.pkg, label: labels[s.pkg] || s.pkg, ms: 0, opens: 0, longestMs: 0, hours: new Array(24).fill(0) }; byPkg.set(s.pkg, a); }
    const ms = s.end - s.start;
    a.ms += ms; a.opens += 1; a.longestMs = Math.max(a.longestMs, ms);
    addToHours(a.hours, s.start, s.end, dayStart);
    addToHours(hours, s.start, s.end, dayStart);
  }
  const apps = [...byPkg.values()].sort((x, y) => y.ms - x.ms).slice(0, DAY_APPS)
    .map((a) => ({ ...a, ms: Math.round(a.ms), longestMs: Math.round(a.longestMs), hours: a.hours.map(Math.round) }));
  const index = new Map(apps.map((a, i) => [a.pkg, i]));
  const sess = sessions.filter((s) => index.has(s.pkg)).slice(-MAX_SESS)
    .map((s) => [index.get(s.pkg), Math.round((s.start - dayStart) / 1000), Math.max(1, Math.round((s.end - s.start) / 1000))]);
  const pickupHours = new Array(24).fill(0);
  taps.forEach((t) => { const h = Math.floor((t - dayStart) / HOUR); if (h >= 0 && h < 24) pickupHours[h] += 1; });
  const total = sessions.reduce((sum, s) => sum + (s.end - s.start), 0);
  return {
    date, v: 2, at,
    totalMs: Math.round(total),
    opens: sessions.length,
    pickups: taps.length,
    firstAt: sessions.length ? sessions[0].start : null,
    lastAt: sessions.length ? Math.max(...sessions.map((s) => s.end)) : null,
    longestMs: Math.round(sessions.reduce((m, s) => Math.max(m, s.end - s.start), 0)),
    hours: hours.map(Math.round),
    pickupHours,
    apps,
    sess,
  };
}

/** A day's sessions back as absolute times: [{ pkg, label, start, end }]. `pkg` narrows to one app. v1 → []. */
export function daySessions(day, pkg = null) {
  if (!day || !Array.isArray(day.sess) || !Array.isArray(day.apps)) return [];
  const [dayStart] = phoneDayBounds(day.date);
  const out = [];
  for (const [i, startSec, durSec] of day.sess) {
    const a = day.apps[i];
    if (!a || (pkg && a.pkg !== pkg)) continue;
    const start = dayStart + startSec * 1000;
    out.push({ pkg: a.pkg, label: a.label, start, end: start + durSec * 1000 });
  }
  return out;
}

// ── Goals and the score ──────────────────────────────────────────────────────────────────────────
/** The apps that count as distracting: the goals' own list if set, else every app with a focus rule. */
export function distractingApps(focusCfg) {
  const own = focusCfg?.goals?.apps;
  if (Array.isArray(own)) return new Set(own);
  return new Set((focusCfg?.rules || []).map((r) => r && r.pkg).filter(Boolean));
}

export function phoneGoals(focusCfg) {
  const g = focusCfg?.goals || {};
  const pick = (k) => (Number.isFinite(g[k]) && g[k] > 0 ? g[k] : PHONE_GOAL_DEFAULTS[k]);
  return { screenMin: pick('screenMin'), distractMin: pick('distractMin'), pickups: pick('pickups') };
}

/** { screen: ms, distract: ms, pickups: n | null } for a day snapshot (v1 has no pickups). */
export function phoneDayMetrics(day, distract = new Set()) {
  if (!day) return null;
  const apps = Array.isArray(day.apps) ? day.apps : [];
  return {
    screen: day.totalMs || 0,
    distract: apps.filter((a) => distract.has(a.pkg)).reduce((s, a) => s + (a.ms || 0), 0),
    pickups: Number.isFinite(day.pickups) ? day.pickups : null,
  };
}

/** One metric against its goal: 0 → 100, goal → 80, 2 × goal → 0. */
export function metricScore(value, goal) {
  if (!(goal > 0) || value == null || !Number.isFinite(value)) return null;
  const r = Math.max(0, value) / goal;
  const s = r <= 1 ? 100 - 20 * r : 80 - 80 * (r - 1);
  return Math.round(Math.max(0, Math.min(100, s)));
}

export function scoreZone(score) {
  if (score == null) return null;
  return score >= 67 ? 'green' : score >= 34 ? 'yellow' : 'red';
}

const goalValue = (m, goals) => (m.unit === 'ms' ? goals[m.goalKey] * 60000 : goals[m.goalKey]);

/** The day score: { score, zone, parts: [{ key, label, value, goal, score, weight, unit, met }] } or null. */
export function phoneScore(day, goals, distract) {
  const v = phoneDayMetrics(day, distract);
  if (!v) return null;
  const parts = PHONE_METRICS.map((m) => {
    const goal = goalValue(m, goals);
    const value = v[m.key];
    const score = metricScore(value, goal);
    return { key: m.key, label: m.label, unit: m.unit, value, goal, score, weight: m.weight, met: value != null && value <= goal };
  });
  const used = parts.filter((p) => p.score != null);
  const w = used.reduce((s, p) => s + p.weight, 0);
  if (!w) return null;
  const score = Math.round(used.reduce((s, p) => s + p.score * p.weight, 0) / w);
  return { score, zone: scoreZone(score), parts };
}

const median = (xs) => {
  const a = xs.filter((x) => x != null && Number.isFinite(x)).sort((p, q) => p - q);
  if (!a.length) return null;
  const m = Math.floor(a.length / 2);
  return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

/** Your normal: the median of each metric over the `n` days before `date` that have data. */
export function phoneBaseline(daysByDate, date, distract, n = 14) {
  const vals = [];
  for (let i = 1; i <= n; i++) {
    const d = daysByDate[addDaysStr(date, -i)];
    if (d) vals.push(phoneDayMetrics(d, distract));
  }
  if (!vals.length) return null;
  return {
    days: vals.length,
    screen: median(vals.map((x) => x.screen)),
    distract: median(vals.map((x) => x.distract)),
    pickups: median(vals.map((x) => x.pickups)),
  };
}

/** Goals from your last two weeks: 10 % under your median, minutes rounded to 15, pickups to 5. */
export function suggestPhoneGoals(daysByDate, today, distract) {
  const b = phoneBaseline(daysByDate, addDaysStr(today, 1), distract, 14);
  if (!b) return null;
  const mins = (ms, floor) => Math.max(floor, Math.round((ms * 0.9) / 60000 / 15) * 15);
  return {
    screenMin: b.screen != null ? mins(b.screen, 30) : PHONE_GOAL_DEFAULTS.screenMin,
    distractMin: b.distract != null ? mins(b.distract, 15) : PHONE_GOAL_DEFAULTS.distractMin,
    pickups: b.pickups != null ? Math.max(10, Math.round((b.pickups * 0.9) / 5) * 5) : PHONE_GOAL_DEFAULTS.pickups,
    basedOnDays: b.days,
  };
}

// ── Series and summaries ─────────────────────────────────────────────────────────────────────────
/** One row per date in [from, to]: { date, has, screen, distract, pickups, score, zone }. Missing days → has:false. */
export function phoneSeries(daysByDate, from, to, goals, distract) {
  return dateRange(from, to).map((date) => {
    const d = daysByDate[date];
    if (!d) return { date, has: false, screen: null, distract: null, pickups: null, score: null, zone: null };
    const m = phoneDayMetrics(d, distract);
    const sc = phoneScore(d, goals, distract);
    return { date, has: true, ...m, score: sc?.score ?? null, zone: sc?.zone ?? null };
  });
}

/** Averages, best / worst day, goal-met days, current streak, and the change against the period before. */
export function periodSummary(series, prevSeries = []) {
  const has = series.filter((r) => r.has);
  const avg = (rows, k) => { const v = rows.map((r) => r[k]).filter((x) => x != null); return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null; };
  const cur = { screen: avg(has, 'screen'), distract: avg(has, 'distract'), pickups: avg(has, 'pickups'), score: avg(has, 'score') };
  const prevHas = prevSeries.filter((r) => r.has);
  const prev = { screen: avg(prevHas, 'screen'), distract: avg(prevHas, 'distract'), pickups: avg(prevHas, 'pickups'), score: avg(prevHas, 'score') };
  const change = {};
  for (const k of Object.keys(cur)) change[k] = cur[k] != null && prev[k] ? (cur[k] - prev[k]) / prev[k] : null;
  const scored = has.filter((r) => r.score != null);
  const best = scored.reduce((b, r) => (!b || r.score > b.score ? r : b), null);
  const worst = scored.reduce((b, r) => (!b || r.score < b.score ? r : b), null);
  let streak = 0;
  for (let i = series.length - 1; i >= 0; i--) {
    const r = series[i];
    if (!r.has) { if (i === series.length - 1) continue; break; }   // today may not be in yet
    if (r.zone === 'green') streak += 1; else break;
  }
  return {
    days: has.length, avg: cur, prev, change, best, worst, streak,
    green: scored.filter((r) => r.zone === 'green').length,
    yellow: scored.filter((r) => r.zone === 'yellow').length,
    red: scored.filter((r) => r.zone === 'red').length,
  };
}

/** Apps over a period: [{ pkg, label, ms, opens, days, longestMs }] most used first. */
export function appTotals(days) {
  const m = new Map();
  for (const d of days || []) {
    for (const a of d?.apps || []) {
      const cur = m.get(a.pkg) || { pkg: a.pkg, label: a.label || a.pkg, ms: 0, opens: 0, days: 0, longestMs: 0 };
      cur.ms += a.ms || 0; cur.opens += a.opens || 0; cur.days += (a.ms || 0) > 0 ? 1 : 0;
      cur.longestMs = Math.max(cur.longestMs, a.longestMs || 0);
      if (a.label) cur.label = a.label;
      m.set(a.pkg, cur);
    }
  }
  return [...m.values()].sort((x, y) => y.ms - x.ms);
}

/** One app per date in [from, to]: { date, has, ms, opens, longestMs }. */
export function appSeries(daysByDate, pkg, from, to) {
  return dateRange(from, to).map((date) => {
    const d = daysByDate[date];
    const a = d?.apps?.find((x) => x.pkg === pkg);
    return { date, has: !!d, ms: a ? a.ms : d ? 0 : null, opens: a ? a.opens : d ? 0 : null, longestMs: a?.longestMs || 0 };
  });
}

/**
 * Average ms per hour of the day over the days that have hour data (v2): [24]. With `pkg`, that app only;
 * with `distract`, returns { all, distract } so the report can stack them.
 */
export function hourProfile(days, { pkg = null, distract = null } = {}) {
  const withHours = (days || []).filter((d) => Array.isArray(d?.hours));
  const all = new Array(24).fill(0);
  const dis = new Array(24).fill(0);
  for (const d of withHours) {
    const src = pkg ? d.apps?.find((a) => a.pkg === pkg)?.hours : d.hours;
    if (src) src.forEach((ms, h) => { all[h] += ms || 0; });
    if (distract && !pkg) (d.apps || []).filter((a) => distract.has(a.pkg) && Array.isArray(a.hours)).forEach((a) => a.hours.forEach((ms, h) => { dis[h] += ms || 0; }));
  }
  const n = Math.max(1, withHours.length);
  const avg = all.map((x) => x / n);
  return distract && !pkg ? { all: avg, distract: dis.map((x) => x / n), days: withHours.length } : { all: avg, days: withHours.length };
}

/** Top `n` rows by `key` + one "Other" slice: [{ key, label, value, share }]. */
export function pieSlices(rows, n = 6, key = 'ms') {
  const list = (rows || []).filter((r) => (r[key] || 0) > 0);
  const total = list.reduce((s, r) => s + r[key], 0);
  if (!total) return [];
  const top = list.slice(0, n).map((r) => ({ key: r.pkg, label: r.label, value: r[key], share: r[key] / total }));
  const rest = list.slice(n).reduce((s, r) => s + r[key], 0);
  if (rest > 0) top.push({ key: 'other', label: 'Other', value: rest, share: rest / total });
  return top;
}

/** A month as weeks (Monday first, like weekplan) of 'YYYY-MM-DD' | null, for a calendar chart. month 0-11. */
export function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const days = new Date(year, month + 1, 0).getDate();
  const cells = new Array((first.getDay() + 6) % 7).fill(null);
  for (let d = 1; d <= days; d++) cells.push(localDateStr(new Date(year, month, d)));
  while (cells.length % 7) cells.push(null);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

/** Time in `distract` apps inside the given [start, end] windows (e.g. clocked-in work sessions). v2 only. */
export function phoneMsInWindows(day, distract, windows = []) {
  if (!day || !Array.isArray(day.sess)) return null;
  let ms = 0;
  for (const s of daySessions(day)) {
    if (!distract.has(s.pkg)) continue;
    for (const w of windows) {
      const a = Math.max(s.start, w.start); const b = Math.min(s.end, w.end);
      if (b > a) ms += b - a;
    }
  }
  return ms;
}

// ── Comparisons and trends ───────────────────────────────────────────────────────────────────────
const METRIC_KEYS = ['score', 'screen', 'distract', 'pickups'];

/** Per-day averages over `dates` (only days that have data): { days, score, screen, distract, pickups }. */
export function periodAverages(daysByDate, dates, goals, distract) {
  const rows = dates.map((d) => daysByDate[d]).filter(Boolean).map((day) => {
    const m = phoneDayMetrics(day, distract);
    return { ...m, score: phoneScore(day, goals, distract)?.score ?? null };
  });
  const out = { days: rows.length };
  for (const k of METRIC_KEYS) {
    const v = rows.map((r) => r[k]).filter((x) => x != null);
    out[k] = v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
  }
  return out;
}

const monday = (date) => { const d = new Date(`${date}T12:00:00`); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return localDateStr(d); };
const monthStart = (date) => `${date.slice(0, 7)}-01`;
const prevMonthSameDay = (date) => {
  const d = new Date(`${date}T12:00:00`);
  const day = d.getDate();
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1, 12);
  const last = new Date(p.getFullYear(), p.getMonth() + 1, 0).getDate();
  p.setDate(Math.min(day, last));
  return localDateStr(p);
};

/**
 * Like-for-like comparisons, each as per-day averages so part-weeks compare fairly:
 *   week  — this week so far (Mon → today) vs the same days last week
 *   month — this month so far vs the same days last month
 *   7d / 30d — the last 7 / 30 days vs the 7 / 30 before
 * → [{ key, label, prevLabel, cur, prev, change: { score, screen, distract, pickups } }]  (change = fraction, null if no base)
 */
export function comparePeriods(daysByDate, today, goals, distract) {
  const wk = monday(today);
  const span = dateRange(wk, today).length;
  const ms = monthStart(today);
  const defs = [
    { key: 'week', label: 'This week', prevLabel: 'Last week', cur: [wk, today], prev: [addDaysStr(wk, -7), addDaysStr(wk, span - 8)] },
    { key: 'month', label: 'This month', prevLabel: 'Last month', cur: [ms, today], prev: [monthStart(prevMonthSameDay(today)), prevMonthSameDay(today)] },
    { key: '7d', label: 'Last 7 days', prevLabel: 'The 7 before', cur: [addDaysStr(today, -6), today], prev: [addDaysStr(today, -13), addDaysStr(today, -7)] },
    { key: '30d', label: 'Last 30 days', prevLabel: 'The 30 before', cur: [addDaysStr(today, -29), today], prev: [addDaysStr(today, -59), addDaysStr(today, -30)] },
  ];
  return defs.map((p) => {
    const cur = { from: p.cur[0], to: p.cur[1], ...periodAverages(daysByDate, dateRange(p.cur[0], p.cur[1]), goals, distract) };
    const prev = { from: p.prev[0], to: p.prev[1], ...periodAverages(daysByDate, dateRange(p.prev[0], p.prev[1]), goals, distract) };
    const change = {};
    for (const k of METRIC_KEYS) change[k] = cur[k] != null && prev[k] ? (cur[k] - prev[k]) / prev[k] : null;
    return { key: p.key, label: p.label, prevLabel: p.prevLabel, cur, prev, change };
  });
}

/** Least-squares slope of `key` over the rows that have it, as change per week (units / 7 days). Null under 5 points. */
export function trendPerWeek(series, key) {
  const pts = series.map((r, i) => [i, r[key]]).filter(([, v]) => v != null);
  if (pts.length < 5) return null;
  const n = pts.length;
  const mx = pts.reduce((s, [x]) => s + x, 0) / n;
  const my = pts.reduce((s, [, y]) => s + y, 0) / n;
  const num = pts.reduce((s, [x, y]) => s + (x - mx) * (y - my), 0);
  const den = pts.reduce((s, [x]) => s + (x - mx) ** 2, 0);
  return den ? (num / den) * 7 : null;
}

/** The last `weeks` Mon–Sun weeks (oldest first, this week last) as per-day averages: [{ start, end, days, …metrics }]. */
export function weeklyAverages(daysByDate, today, weeks, goals, distract) {
  const out = [];
  const cur = monday(today);
  for (let i = weeks - 1; i >= 0; i--) {
    const start = addDaysStr(cur, -7 * i);
    const end = i === 0 ? today : addDaysStr(start, 6);
    out.push({ start, end, ...periodAverages(daysByDate, dateRange(start, end), goals, distract) });
  }
  return out;
}

/** One app: per-day average time and opens over `dates` (days with data count, days the app wasn't used = 0). */
export function appPeriod(daysByDate, pkg, dates) {
  const rows = dates.map((d) => daysByDate[d]).filter(Boolean);
  if (!rows.length) return { days: 0, ms: null, opens: null };
  const tot = rows.reduce((s, d) => { const a = d.apps?.find((x) => x.pkg === pkg); return { ms: s.ms + (a?.ms || 0), opens: s.opens + (a?.opens || 0) }; }, { ms: 0, opens: 0 });
  return { days: rows.length, ms: tot.ms / rows.length, opens: tot.opens / rows.length };
}

/** The same four comparisons for one app: [{ key, label, prevLabel, cur, prev, change: { ms, opens } }]. */
export function compareAppPeriods(daysByDate, pkg, today) {
  const base = comparePeriods({}, today, PHONE_GOAL_DEFAULTS, new Set());
  return base.map((p) => {
    const cur = { from: p.cur.from, to: p.cur.to, ...appPeriod(daysByDate, pkg, dateRange(p.cur.from, p.cur.to)) };
    const prev = { from: p.prev.from, to: p.prev.to, ...appPeriod(daysByDate, pkg, dateRange(p.prev.from, p.prev.to)) };
    const ch = (k) => (cur[k] != null && prev[k] ? (cur[k] - prev[k]) / prev[k] : null);
    return { key: p.key, label: p.label, prevLabel: p.prevLabel, cur, prev, change: { ms: ch('ms'), opens: ch('opens') } };
  });
}
